import { useQuery } from '@mint-lib/query-client';
import type { UseQueryResult } from '@mint-lib/query-client';
import { Integrations } from '@mint-sdk/api-omni-trafficking';

/**
 * Ported from
 * `packages/connections/src/components/_organisms/TraffickingAddConnectionForm/useCMExternalData.ts`.
 * Backs `ProfileSelect`/`AdvertiserSelect` in both the add and manage forms.
 */
export interface CMProfileOption {
  id: string;
  name: string;
}

export interface CMAdvertiserOption {
  value: string;
  label: string;
  floodlightConfigId: string;
  disabled?: boolean;
}

export interface UseCMExternalDataParams {
  /** Present while creating a brand-new connection, before it has an id. */
  oauthFlowId?: string | null;
  /** Present once the connection exists (manage form, or add form post-create). */
  connectionId?: string | null;
  profileId: string;
  enableProfile: boolean;
  enableAdvertisers: boolean;
  previousSelectedAdvertiserIds?: string[];
}

export interface UseCMExternalDataResult {
  profiles: CMProfileOption[] | undefined;
  isLoadingProfiles: boolean;
  advertisers: CMAdvertiserOption[] | undefined;
  isLoadingAdvertisers: boolean;
  refetchAdvertisers: UseQueryResult<CMAdvertiserOption[]>['refetch'];
}

export function useCMExternalData(
  params: UseCMExternalDataParams,
): UseCMExternalDataResult {
  const {
    oauthFlowId,
    connectionId,
    profileId,
    enableProfile,
    enableAdvertisers,
    previousSelectedAdvertiserIds,
  } = params;

  // TODO(cm360BackendOwnedAuth): this package still implements the
  // frontend-owned flow (oauthFlowId in localStorage), i.e. the SDK's
  // `*Legacy` endpoints; port the backend-owned flow before the flag is removed.
  const { data: profiles, isLoading: isLoadingProfiles } = useQuery({
    ...Integrations.getCMProfilesLegacy({
      params: {
        oauthFlowId: oauthFlowId ?? null,
        integrationId: connectionId ?? null,
      },
    }),
    enabled: enableProfile,
  });

  const {
    data: advertisers,
    isLoading: isLoadingAdvertisers,
    refetch: refetchAdvertisers,
  } = useQuery({
    ...Integrations.getCMAdvertisersLegacy({
      params: {
        oauthFlowId: oauthFlowId ?? null,
        integrationId: connectionId ?? null,
        profileId: profileId || '',
      },
    }),
    select: (data) =>
      (data ?? []).map((ad) => ({
        label: ad.name,
        value: ad.id,
        floodlightConfigId: ad.floodlightConfigId,
        disabled: previousSelectedAdvertiserIds?.includes(ad.id),
      })),
    enabled: enableAdvertisers && !!profileId,
  });

  return {
    profiles,
    isLoadingProfiles,
    advertisers,
    isLoadingAdvertisers,
    refetchAdvertisers,
  };
}
